import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, FileText, Download, Shield, Building2, User, ArrowRight, ArrowLeft, Search, CalendarDays, Receipt } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const STATES_MY = [
  'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang',
  'Penang', 'Perak', 'Perlis', 'Sabah', 'Sarawak', 'Selangor',
  'Terengganu', 'Kuala Lumpur', 'Labuan', 'Putrajaya',
];

export default function QRGuestForm() {
  const { token } = useParams();
  const [pageState, setPageState] = useState('loading');
  const [formData, setFormData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [step, setStep] = useState(1);

  const [buyerType, setBuyerType] = useState('');
  const [guest, setGuest] = useState({
    name: '', email: '', phone: '', id_type: 'NRIC', id_value: '',
    tin: '', brn: '', sst_no: '',
    address: '', city: '', state: '', postal_code: '', country: 'MYS',
    description: '', booking_reference: '',
  });

  const [result, setResult] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [submissionError, setSubmissionError] = useState('');

  // Booking lookup state
  const [lookingUp, setLookingUp] = useState(false);
  const [bookingData, setBookingData] = useState(null); // null=not looked up, {found:true,...} or {found:false}
  const [lookupError, setLookupError] = useState('');

  const isGeneral = formData?.qr_type === 'general';

  useEffect(() => {
    if (!token) { setPageState('invalid'); setErrorMsg('No token provided.'); return; }
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const { data } = await axios.get(`${API}/api/einvoice/qr/form/${token}`);
      setFormData(data);
      if (data.description) setGuest(g => ({ ...g, description: data.description }));
      if (data.booking_reference) setGuest(g => ({ ...g, booking_reference: data.booking_reference }));
      setPageState('form');
    } catch (err) {
      const detail = err.response?.data?.detail || 'This QR link is invalid or has expired.';
      setErrorMsg(detail);
      setPageState('invalid');
    }
  };

  const updateGuest = (field, value) => setGuest(g => ({ ...g, [field]: value }));

  const lookupBooking = async () => {
    const ref = guest.booking_reference.trim();
    if (!ref) return;
    setLookingUp(true);
    setLookupError('');
    setBookingData(null);
    try {
      const { data } = await axios.get(`${API}/api/einvoice/qr/form/${token}/lookup-booking?ref=${encodeURIComponent(ref)}`);
      setBookingData(data);
      if (data.found) {
        // Auto-fill from booking
        setGuest(g => ({
          ...g,
          name: data.guest_name || g.name,
          email: data.guest_email || g.email,
          description: data.description || g.description,
        }));
      } else {
        setLookupError(data.message || 'Booking not found. Please check your booking reference or contact front desk.');
      }
    } catch (err) {
      setLookupError('Unable to look up booking. Please try again.');
    } finally { setLookingUp(false); }
  };

  const handleSubmit = async () => {
    setPageState('submitting');
    setSubmissionError('');
    try {
      const payload = {
        buyer_type: buyerType,
        name: guest.name.trim(),
        email: guest.email.trim(),
        phone: guest.phone.trim() || null,
        id_type: guest.id_type,
        id_value: guest.id_value.trim() || null,
        tin: guest.tin.trim() || null,
        brn: buyerType === 'company' ? (guest.brn.trim() || null) : null,
        sst_no: buyerType === 'company' ? (guest.sst_no.trim() || null) : null,
        address: guest.address.trim() || null,
        city: guest.city.trim() || null,
        state: guest.state || null,
        postal_code: guest.postal_code.trim() || null,
        country: guest.country || 'MYS',
        description: guest.description.trim() || null,
        booking_reference: guest.booking_reference.trim() || null,
      };
      const { data } = await axios.post(`${API}/api/einvoice/qr/form/${token}/submit`, payload);
      setResult(data);
      await pollStatus();
      setPageState('status');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setSubmissionError(detail);
      } else if (Array.isArray(detail)) {
        setSubmissionError(detail.map(d => d.msg || d.detail || JSON.stringify(d)).join('. '));
      } else {
        setSubmissionError('Submission failed. Please check your details and try again.');
      }
      setPageState('form');
      setStep(2);
    }
  };

  const pollStatus = async () => {
    try {
      const params = new URLSearchParams();
      if (isGeneral && guest.booking_reference) params.append('booking_ref', guest.booking_reference.trim());
      const { data } = await axios.get(`${API}/api/einvoice/qr/form/${token}/status?${params}`);
      setStatusData(data);
    } catch (e) { console.debug('QR status poll failed', e); }
  };

  useEffect(() => {
    if (pageState !== 'status') return;
    const interval = setInterval(pollStatus, 5000);
    return () => clearInterval(interval);
  }, [pageState]);

  const downloadPdf = () => {
    const params = new URLSearchParams();
    if (isGeneral && guest.booking_reference) params.append('booking_ref', guest.booking_reference.trim());
    window.open(`${API}/api/einvoice/qr/form/${token}/pdf?${params}`, '_blank');
  };

  // Resolved amount: system-only
  const resolvedAmount = isGeneral
    ? (bookingData?.found ? bookingData.amount : null)
    : formData?.amount;

  const canProceedStep2 = buyerType !== '';
  const canProceedStep3 = (() => {
    if (!guest.name.trim() || !guest.email.trim()) return false;
    if (!guest.state) return false;
    if (isGeneral) {
      if (!guest.booking_reference.trim()) return false;
      if (!bookingData?.found) return false; // Must have successful lookup
    }
    if (buyerType === 'company' && !guest.brn.trim()) return false;
    return true;
  })();

  // ─── Render States ───
  if (pageState === 'loading') {
    return <PageShell><div className="flex flex-col items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#4169E1] mb-4" /><p className="text-sm text-slate-500">Validating your link...</p></div></PageShell>;
  }

  if (pageState === 'invalid') {
    return (
      <PageShell>
        <Card className="max-w-md mx-auto mt-12 border-red-200 bg-red-50">
          <CardContent className="py-10 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-red-700 mb-2" data-testid="invalid-token-title">Invalid Link</h2>
            <p className="text-sm text-red-600" data-testid="invalid-token-msg">{errorMsg}</p>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  if (pageState === 'submitting') {
    return <PageShell><div className="flex flex-col items-center justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-[#4169E1] mb-4" /><p className="text-base font-medium text-slate-700" data-testid="submitting-label">Submitting your e-Invoice...</p><p className="text-xs text-slate-400 mt-1">This may take a few seconds</p></div></PageShell>;
  }

  if (pageState === 'status') {
    return <PageShell><StatusScreen result={result} statusData={statusData} onDownloadPdf={downloadPdf} onPollStatus={pollStatus} /></PageShell>;
  }

  // ─── Form ───
  return (
    <PageShell>
      <div className="max-w-lg mx-auto" data-testid="qr-guest-form">
        {formData && (
          <div className="text-center mb-6">
            {formData.business_name && <p className="text-sm text-slate-500 mb-0.5">{formData.business_name}</p>}
            <h2 className="text-lg font-bold text-slate-800" data-testid="property-name">{formData.property_name}</h2>
            {!isGeneral && formData.booking_reference && (
              <p className="text-xs text-slate-400 mt-1">Booking Ref: {formData.booking_reference}</p>
            )}
            {/* Booking QR: show preset amount (read-only from merchant) */}
            {!isGeneral && formData.amount && (
              <div className="mt-3 inline-block bg-[#4169E1]/5 border border-[#4169E1]/20 rounded-lg px-5 py-2">
                <span className="text-xs text-slate-500 block">Invoice Amount</span>
                <span className="text-xl font-bold text-[#4169E1]" data-testid="form-amount">RM {parseFloat(formData.amount).toFixed(2)}</span>
              </div>
            )}
            {isGeneral && (
              <p className="text-xs text-slate-400 mt-2">Enter your booking reference to retrieve invoice details</p>
            )}
          </div>
        )}

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8" data-testid="step-indicator">
          {[1, 2, 3].map(s => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                s < step ? 'bg-green-500 text-white' :
                s === step ? 'bg-[#4169E1] text-white shadow-md shadow-[#4169E1]/30' :
                'bg-slate-200 text-slate-400'
              }`}>{s < step ? <CheckCircle2 className="w-4 h-4" /> : s}</div>
              {s < 3 && <div className={`w-10 h-0.5 ${s < step ? 'bg-green-400' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {submissionError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="submission-error">
            <p className="text-sm text-red-700 flex items-start gap-2">
              <XCircle className="w-4 h-4 mt-0.5 shrink-0" />{submissionError}
            </p>
          </div>
        )}

        <Card className="shadow-sm border-slate-200">
          <CardContent className="pt-6 pb-6">
            {/* Step 1 */}
            {step === 1 && (
              <div className="space-y-4" data-testid="step-1">
                <div className="text-center mb-2">
                  <h3 className="text-base font-semibold text-slate-800">How are you billing?</h3>
                  <p className="text-xs text-slate-400">Select your billing category</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setBuyerType('individual')} className={`p-5 rounded-xl border-2 transition-all text-center ${buyerType === 'individual' ? 'border-[#4169E1] bg-[#4169E1]/5 shadow-sm' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-testid="buyer-type-individual">
                    <User className={`w-8 h-8 mx-auto mb-2 ${buyerType === 'individual' ? 'text-[#4169E1]' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${buyerType === 'individual' ? 'text-[#4169E1]' : 'text-slate-600'}`}>Individual</span>
                    <p className="text-[10px] text-slate-400 mt-1">Personal billing</p>
                  </button>
                  <button onClick={() => setBuyerType('company')} className={`p-5 rounded-xl border-2 transition-all text-center ${buyerType === 'company' ? 'border-[#4169E1] bg-[#4169E1]/5 shadow-sm' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-testid="buyer-type-company">
                    <Building2 className={`w-8 h-8 mx-auto mb-2 ${buyerType === 'company' ? 'text-[#4169E1]' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${buyerType === 'company' ? 'text-[#4169E1]' : 'text-slate-600'}`}>Company</span>
                    <p className="text-[10px] text-slate-400 mt-1">Business billing</p>
                  </button>
                </div>
                <Button onClick={() => setStep(2)} disabled={!canProceedStep2} className="w-full bg-[#4169E1] hover:bg-[#3558c8] text-white mt-2" data-testid="next-step-1">
                  Continue <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="space-y-4" data-testid="step-2">
                <div className="text-center mb-2">
                  <h3 className="text-base font-semibold text-slate-800">{buyerType === 'company' ? 'Company Details' : 'Personal Details'}</h3>
                  <p className="text-xs text-slate-400">Required for LHDN e-Invoice issuance</p>
                </div>

                <div className="space-y-3">
                  {/* Booking Reference + Lookup for General QR */}
                  {isGeneral && (
                    <div>
                      <Label className="text-xs text-slate-600 mb-1 block">Booking Reference *</Label>
                      <div className="flex gap-2">
                        <Input
                          value={guest.booking_reference}
                          onChange={e => { updateGuest('booking_reference', e.target.value); setBookingData(null); setLookupError(''); }}
                          placeholder="e.g. BK-2026-001"
                          className="flex-1"
                          data-testid="guest-booking-ref"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 px-3"
                          onClick={lookupBooking}
                          disabled={lookingUp || !guest.booking_reference.trim()}
                          data-testid="lookup-booking-btn"
                        >
                          {lookingUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                        </Button>
                      </div>

                      {/* Lookup error */}
                      {lookupError && (
                        <div className="mt-2 p-2.5 bg-red-50 border border-red-200 rounded-lg" data-testid="lookup-error">
                          <p className="text-xs text-red-700 flex items-start gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            {lookupError}
                          </p>
                        </div>
                      )}

                      {/* Booking Summary Card */}
                      {bookingData?.found && (
                        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="booking-summary-card">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <span className="text-xs font-semibold text-green-700">Booking Found</span>
                          </div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span className="text-green-600">Booking</span>
                              <span className="font-medium text-green-800" data-testid="summary-booking-ref">{bookingData.booking_id || guest.booking_reference}</span>
                            </div>
                            {bookingData.amount && (
                              <div className="flex justify-between">
                                <span className="text-green-600">Amount</span>
                                <span className="font-bold text-green-800" data-testid="summary-amount">RM {parseFloat(bookingData.amount).toFixed(2)}</span>
                              </div>
                            )}
                            {bookingData.description && (
                              <div className="flex justify-between">
                                <span className="text-green-600">Description</span>
                                <span className="text-green-800" data-testid="summary-desc">{bookingData.description}</span>
                              </div>
                            )}
                            {bookingData.check_in && (
                              <div className="flex justify-between">
                                <span className="text-green-600">Dates</span>
                                <span className="text-green-800">{bookingData.check_in} - {bookingData.check_out || '...'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <FieldRow label={buyerType === 'company' ? 'Company Name *' : 'Full Name *'}>
                    <Input value={guest.name} onChange={e => updateGuest('name', e.target.value)} placeholder={buyerType === 'company' ? 'Acme Sdn Bhd' : 'Ahmad bin Ali'} data-testid="guest-name" />
                  </FieldRow>

                  <FieldRow label="Email Address *">
                    <Input type="email" value={guest.email} onChange={e => updateGuest('email', e.target.value)} placeholder="email@example.com" data-testid="guest-email" />
                  </FieldRow>

                  <FieldRow label="Phone Number">
                    <Input value={guest.phone} onChange={e => updateGuest('phone', e.target.value)} placeholder="+60 12-345 6789" data-testid="guest-phone" />
                  </FieldRow>

                  <div className="grid grid-cols-2 gap-3">
                    <FieldRow label="ID Type">
                      <select value={guest.id_type} onChange={e => updateGuest('id_type', e.target.value)} className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white appearance-none" style={{WebkitAppearance: 'menulist'}} data-testid="guest-id-type">
                        <option value="NRIC">NRIC</option>
                        <option value="PASSPORT">Passport</option>
                        <option value="BRN">BRN</option>
                        <option value="ARMY">Army ID</option>
                      </select>
                    </FieldRow>
                    <FieldRow label="ID Number">
                      <Input value={guest.id_value} onChange={e => updateGuest('id_value', e.target.value)} placeholder="880101-14-5678" data-testid="guest-id-value" />
                    </FieldRow>
                  </div>

                  <FieldRow label="Tax Identification Number (TIN)">
                    <Input value={guest.tin} onChange={e => updateGuest('tin', e.target.value)} placeholder="e.g. C12345678010" data-testid="guest-tin" />
                    <p className="text-[10px] text-slate-400 mt-0.5">Leave empty if not applicable</p>
                  </FieldRow>

                  {buyerType === 'company' && (
                    <>
                      <FieldRow label="Business Registration No. (BRN) *">
                        <Input value={guest.brn} onChange={e => updateGuest('brn', e.target.value)} placeholder="202001012345" data-testid="guest-brn" />
                      </FieldRow>
                      <FieldRow label="SST Registration No.">
                        <Input value={guest.sst_no} onChange={e => updateGuest('sst_no', e.target.value)} placeholder="A01-2345-67891011" data-testid="guest-sst" />
                      </FieldRow>
                    </>
                  )}

                  <div className="border-t border-slate-100 pt-3 mt-3">
                    <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">Address</p>
                  </div>

                  <FieldRow label="Address Line">
                    <Input value={guest.address} onChange={e => updateGuest('address', e.target.value)} placeholder="123, Jalan ABC" data-testid="guest-address" />
                  </FieldRow>

                  <div className="grid grid-cols-2 gap-3">
                    <FieldRow label="City">
                      <Input value={guest.city} onChange={e => updateGuest('city', e.target.value)} placeholder="Kuala Lumpur" data-testid="guest-city" />
                    </FieldRow>
                    <FieldRow label="Postal Code">
                      <Input value={guest.postal_code} onChange={e => updateGuest('postal_code', e.target.value)} placeholder="50000" data-testid="guest-postal" />
                    </FieldRow>
                  </div>

                  <FieldRow label="State *">
                    <select
                      value={guest.state}
                      onChange={e => updateGuest('state', e.target.value)}
                      className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white appearance-none"
                      style={{WebkitAppearance: 'menulist', MozAppearance: 'menulist'}}
                      data-testid="guest-state"
                    >
                      <option value="">Select state</option>
                      {STATES_MY.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </FieldRow>

                  {/* No Amount input - Amount is system-only */}

                  <FieldRow label="Description">
                    <Input value={guest.description} onChange={e => updateGuest('description', e.target.value)} placeholder="Accommodation charges" data-testid="guest-desc" />
                  </FieldRow>
                </div>

                <div className="flex gap-3 mt-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="back-step-2">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                  <Button onClick={() => setStep(3)} disabled={!canProceedStep3} className="flex-1 bg-[#4169E1] hover:bg-[#3558c8] text-white" data-testid="next-step-2">
                    Review <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div className="space-y-4" data-testid="step-3">
                <div className="text-center mb-2">
                  <h3 className="text-base font-semibold text-slate-800">Review & Submit</h3>
                  <p className="text-xs text-slate-400">Please verify your details before submission</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                  <ReviewRow label="Billing Type" value={buyerType === 'company' ? 'Company' : 'Individual'} />
                  {guest.booking_reference && <ReviewRow label="Booking Ref" value={guest.booking_reference} />}
                  <ReviewRow label="Name" value={guest.name} />
                  <ReviewRow label="Email" value={guest.email} />
                  {guest.phone && <ReviewRow label="Phone" value={guest.phone} />}
                  <ReviewRow label="ID" value={`${guest.id_type}: ${guest.id_value || 'N/A'}`} />
                  {guest.tin && <ReviewRow label="TIN" value={guest.tin} />}
                  {buyerType === 'company' && guest.brn && <ReviewRow label="BRN" value={guest.brn} />}
                  {buyerType === 'company' && guest.sst_no && <ReviewRow label="SST No." value={guest.sst_no} />}
                  {guest.address && <ReviewRow label="Address" value={[guest.address, guest.city, guest.state, guest.postal_code].filter(Boolean).join(', ')} />}
                  <ReviewRow label="State" value={guest.state || 'N/A'} />
                  <div className="border-t border-slate-200 pt-2 mt-2">
                    <ReviewRow label="Description" value={guest.description || formData?.description || 'Accommodation charges'} />
                    <ReviewRow label="Amount" value={resolvedAmount ? `RM ${parseFloat(resolvedAmount).toFixed(2)}` : 'Determined by system'} bold />
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-700 flex items-start gap-2">
                    <Shield className="w-4 h-4 shrink-0 mt-0.5" />
                    By submitting, you authorize the generation of an LHDN-compliant e-Invoice. This action cannot be undone.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(2)} className="flex-1" data-testid="back-step-3">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button onClick={handleSubmit} className="flex-1 bg-[#4169E1] hover:bg-[#3558c8] text-white" data-testid="submit-btn">
                    Submit e-Invoice
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
}

// ─── Sub-components ───

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#4169E1] rounded-lg flex items-center justify-center">
              <span className="text-white text-xs font-bold">S</span>
            </div>
            <span className="text-base font-bold text-slate-800 tracking-tight">StayMii</span>
          </div>
          <span className="text-[10px] text-slate-400 font-medium" data-testid="form-title">e-Invoice Request Form</span>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-6">{children}</main>
      <footer className="border-t border-slate-100 mt-12">
        <div className="max-w-2xl mx-auto px-4 py-4 text-center">
          <p className="text-[10px] text-slate-400" data-testid="trust-footer">Powered by StayMii &bull; Compliant with Lembaga Hasil Dalam Negeri Malaysia (LHDN)</p>
        </div>
      </footer>
    </div>
  );
}

function StatusScreen({ result, statusData, onDownloadPdf, onPollStatus }) {
  const lhdnStatus = statusData?.lhdn_status || (result?.lhdn_submitted ? 'submitted' : 'pending');
  const isValidated = lhdnStatus === 'validated' || lhdnStatus === 'submitted';
  const isRejected = lhdnStatus === 'rejected';
  const isPending = !isValidated && !isRejected;

  return (
    <div className="max-w-md mx-auto mt-8" data-testid="status-screen">
      <Card className={`border-2 ${isRejected ? 'border-red-200' : isValidated ? 'border-green-200' : 'border-blue-200'}`}>
        <CardContent className="py-8 text-center">
          {isValidated && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-4"><CheckCircle2 className="w-8 h-8 text-green-500" /></div>
              <h2 className="text-lg font-bold text-green-700 mb-1" data-testid="status-title">Your e-Invoice is Ready</h2>
              <p className="text-sm text-slate-500 mb-1">Successfully submitted to LHDN MyInvois</p>
              {(statusData?.invoice_number || result?.invoice_number) && <p className="text-xs text-slate-400 mb-4 font-mono" data-testid="invoice-number">Invoice: {statusData?.invoice_number || result?.invoice_number}</p>}
              {(statusData?.lhdn_reference || result?.lhdn_uuid) && (
                <div className="bg-green-50 rounded-lg p-3 mb-4 text-left">
                  <p className="text-[10px] text-green-600 uppercase font-bold mb-1">LHDN Reference</p>
                  <p className="text-xs text-green-800 font-mono break-all" data-testid="lhdn-uuid">{statusData?.lhdn_reference || result?.lhdn_uuid}</p>
                </div>
              )}
              <div className="flex gap-3 mt-4">
                {statusData?.verification_url && (
                  <Button variant="outline" className="flex-1 text-green-700 border-green-200 hover:bg-green-50" onClick={() => window.open(statusData.verification_url, '_blank')} data-testid="view-einvoice-btn"><FileText className="w-4 h-4 mr-1.5" /> View</Button>
                )}
                <Button className="flex-1 bg-[#4169E1] hover:bg-[#3558c8] text-white" onClick={onDownloadPdf} data-testid="download-pdf-btn"><Download className="w-4 h-4 mr-1.5" /> Download PDF</Button>
              </div>
            </>
          )}
          {isPending && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-blue-50 flex items-center justify-center mb-4"><Loader2 className="w-8 h-8 text-[#4169E1] animate-spin" /></div>
              <h2 className="text-lg font-bold text-[#4169E1] mb-1" data-testid="status-title">Your e-Invoice is Being Processed</h2>
              <p className="text-sm text-slate-500 mb-4">We are submitting your invoice to LHDN. This page will update automatically.</p>
              {(statusData?.invoice_number || result?.invoice_number) && <p className="text-xs text-slate-400 font-mono" data-testid="invoice-number">Invoice: {statusData?.invoice_number || result?.invoice_number}</p>}
              <Button variant="outline" className="mt-4" onClick={onPollStatus} data-testid="refresh-status-btn"><Loader2 className="w-3.5 h-3.5 mr-1.5" /> Check Status</Button>
            </>
          )}
          {isRejected && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4"><XCircle className="w-8 h-8 text-red-500" /></div>
              <h2 className="text-lg font-bold text-red-700 mb-1" data-testid="status-title">Submission Failed</h2>
              <p className="text-sm text-slate-500 mb-4">The LHDN system rejected this e-Invoice. Please contact the property for assistance.</p>
              {(statusData?.invoice_number || result?.invoice_number) && <p className="text-xs text-slate-400 font-mono" data-testid="invoice-number">Invoice: {statusData?.invoice_number || result?.invoice_number}</p>}
            </>
          )}
        </CardContent>
      </Card>
      <p className="text-[10px] text-slate-400 text-center mt-4">A copy has been sent to your email address.</p>
    </div>
  );
}

function FieldRow({ label, children }) {
  return <div><Label className="text-xs text-slate-600 mb-1 block">{label}</Label>{children}</div>;
}

function ReviewRow({ label, value, bold }) {
  return (
    <div className="flex justify-between items-start">
      <span className="text-slate-500 text-xs">{label}</span>
      <span className={`text-slate-800 text-right max-w-[60%] ${bold ? 'font-bold text-sm' : 'text-xs'}`}>{value}</span>
    </div>
  );
}
